import React, { useState, useEffect } from "react"
import { useNavigate, Link } from "react-router-dom"
import { getAPIBaseURL } from "../services/helpers"
import { postData } from "../services/request"
import { getApiErrorMessage } from "../services/apiErrorMessage"
import { validateEmail, validatePassword, validateOrganization, validatePhoneNumber, validateAddress, validateCountry } from "../services/validators"
import { countries } from "../data"
import AuthLayout from "../components/AuthLayout"

const HospitalSignup = () => {
    const navigate = useNavigate()
    const [organization, setOrganization] = useState("")
    const [email, setEmail] = useState("")
    const [phoneNumber, setPhoneNumber] = useState("")
    const [password, setPassword] = useState("")
    const [confirmPassword, setConfirmPassword] = useState("")
    const [country, setCountry] = useState("NG")
    const [address, setAddress] = useState("")
    const [btnState, setBtnState] = useState("Sign up")
    const [error, setError] = useState([
        { field: "organization", msg: "" },
        { field: "email", msg: "" },
        { field: "phoneNumber", msg: "" },
        { field: "password", msg: "" },
        { field: "confirmPassword", msg: "" },
        { field: "country", msg: "" },
        { field: "address", msg: "" }
    ])
    const [genError, setGenError] = useState("")
    const [loadingCountries] = useState(false)
    const [showPassword, setShowPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)

    useEffect(() => {
        setCountry("NG")
    }, [])

    const validateForm = () => {
        let isValid = true
        const newErrors = [...error]

        // Validate organization
        const orgValidation = validateOrganization(organization)
        const orgErrorIndex = newErrors.findIndex(item => item.field == "organization")
        if (orgErrorIndex !== -1) {
            newErrors[orgErrorIndex].msg = orgValidation.result
            if (orgValidation.error != "") isValid = false
        }

        // Validate email
        const emailValidation = validateEmail(email)
        const emailErrorIndex = newErrors.findIndex(item => item.field == "email")
        if (emailErrorIndex !== -1) {
            newErrors[emailErrorIndex].msg = emailValidation.result
            if (emailValidation.error != "") isValid = false
        }

        // Validate phone number
        const phoneValidation = validatePhoneNumber(phoneNumber)
        const phoneErrorIndex = newErrors.findIndex(item => item.field == "phoneNumber")
        if (phoneErrorIndex !== -1) {
            newErrors[phoneErrorIndex].msg = phoneValidation.result
            if (phoneValidation.error != "") isValid = false
        }

        // Validate password
        const passwordValidation = validatePassword(password)
        const passwordErrorIndex = newErrors.findIndex(item => item.field == "password")
        if (passwordErrorIndex !== -1) {
            newErrors[passwordErrorIndex].msg = passwordValidation.result
            if (passwordValidation.error != "") isValid = false
        }

        // Validate confirm password
        if (password !== confirmPassword) {
            const confirmPasswordErrorIndex = newErrors.findIndex(item => item.field == "confirmPassword")
            if (confirmPasswordErrorIndex !== -1) {
                newErrors[confirmPasswordErrorIndex].msg = "Passwords do not match"
                isValid = false
            }
        } else {
            const confirmPasswordErrorIndex = newErrors.findIndex(item => item.field == "confirmPassword")
            if (confirmPasswordErrorIndex !== -1) {
                newErrors[confirmPasswordErrorIndex].msg = ""
            }
        }

        // Validate country
        const countryValidation = validateCountry(country)
        const countryErrorIndex = newErrors.findIndex(item => item.field == "country")
        if (countryErrorIndex !== -1) {
            newErrors[countryErrorIndex].msg = countryValidation.result
            if (countryValidation.error != "") isValid = false
        }

        // Validate address
        const addressValidation = validateAddress(address)
        const addressErrorIndex = newErrors.findIndex(item => item.field == "address")
        if (addressErrorIndex !== -1) {
            newErrors[addressErrorIndex].msg = addressValidation.result
            if (addressValidation.error != "") isValid = false
        }

        setError(newErrors)
        return isValid
    }

    const signup = async () => {
        setGenError("")
        if (!validateForm()) {
            return
        }

        setBtnState("Loading...")
        const url = `${getAPIBaseURL()}/v1/users/signup`
        const headers = { 'Content-Type': 'application/x-www-form-urlencoded' }
        const params = new URLSearchParams()
        params.append('role', 'HOSPITAL')
        params.append('organization', organization)
        params.append('email', email)
        params.append('phoneNumber', phoneNumber)
        params.append('password', password)
        params.append('country', country)
        params.append('address', address)

        const requestResult = await postData(url, headers, params.toString())

        setBtnState("Sign up")
        if (requestResult.error == "") {
            if (requestResult.result.status === 201) {
                // Success - redirect to login
                navigate('/login?registered=true')
            } else {
                setGenError(getApiErrorMessage(requestResult, 'Something went wrong'))
            }
        } else {
            setGenError(getApiErrorMessage(requestResult, 'Registration failed'))
        }
    }

    return (
        <AuthLayout title="Create a hospital account" subtitle="Join Medpool to receive and fulfill patient requests.">
                        <div className="space-y-5">
                            {genError != "" ? <div className="text-red-500 text-sm font-semibold p-3 bg-red-50 rounded-lg">{genError}</div> : ""}
                            
                            <div>
                                <label htmlFor="organization" className="block mb-2 text-sm font-medium text-gray-900">Hospital/Organization Name</label>
                                <input value={organization} onChange={(e) => setOrganization(e.target.value)} type="text" name="organization" id="organization" className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5" placeholder="St. Mary's Hospital" required=""/>
                                {error.find(item => item.field == "organization").msg ? <p className="text-red-500 text-sm font-semibold mt-1">{error.find(item => item.field == "organization").msg}</p> : null}
                            </div>

                            <div>
                                <label htmlFor="email" className="block mb-2 text-sm font-medium text-gray-900">Email</label>
                                <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" name="email" id="email" className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5" placeholder="contact@hospital.com" required=""/>
                                {error.find(item => item.field == "email").msg ? <p className="text-red-500 text-sm font-semibold mt-1">{error.find(item => item.field == "email").msg}</p> : null}
                            </div>

                            <div>
                                <label htmlFor="phoneNumber" className="block mb-2 text-sm font-medium text-gray-900">Phone Number</label>
                                <input value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} type="tel" name="phoneNumber" id="phoneNumber" className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5" placeholder="08012345678" required=""/>
                                {error.find(item => item.field == "phoneNumber").msg ? <p className="text-red-500 text-sm font-semibold mt-1">{error.find(item => item.field == "phoneNumber").msg}</p> : null}
                            </div>

                            <div>
                                <label htmlFor="password" className="block mb-2 text-sm font-medium text-gray-900">Password</label>
                                <div className="relative">
                                    <input
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        type={showPassword ? "text" : "password"}
                                        name="password"
                                        id="password"
                                        placeholder="••••••••"
                                        className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full pr-20 p-2.5"
                                        required=""
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute inset-y-0 right-0 flex items-center px-3 text-xs font-medium text-gray-600 hover:text-gray-900"
                                    >
                                        {showPassword ? "Hide" : "Show"}
                                    </button>
                                </div>
                                {error.find(item => item.field == "password").msg ? <p className="text-red-500 text-sm font-semibold mt-1">{error.find(item => item.field == "password").msg}</p> : null}
                            </div>

                            <div>
                                <label htmlFor="confirmPassword" className="block mb-2 text-sm font-medium text-gray-900">Confirm Password</label>
                                <div className="relative">
                                    <input
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        type={showConfirmPassword ? "text" : "password"}
                                        name="confirmPassword"
                                        id="confirmPassword"
                                        placeholder="••••••••"
                                        className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full pr-20 p-2.5"
                                        required=""
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        className="absolute inset-y-0 right-0 flex items-center px-3 text-xs font-medium text-gray-600 hover:text-gray-900"
                                    >
                                        {showConfirmPassword ? "Hide" : "Show"}
                                    </button>
                                </div>
                                {error.find(item => item.field == "confirmPassword").msg ? <p className="text-red-500 text-sm font-semibold mt-1">{error.find(item => item.field == "confirmPassword").msg}</p> : null}
                            </div>

                            <div>
                                <label htmlFor="country" className="block mb-2 text-sm font-medium text-gray-900">Country</label>
                                <select
                                    value={country}
                                    onChange={(e) => setCountry(e.target.value)}
                                    name="country"
                                    id="country"
                                    className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5"
                                    required=""
                                >
                                    <option value="">{loadingCountries ? 'Loading countries...' : 'Select Country'}</option>
                                    {countries.map((c) => (
                                        <option key={c.code} value={c.code}>{c.name}</option>
                                    ))}
                                </select>
                                {error.find(item => item.field == "country").msg ? <p className="text-red-500 text-sm font-semibold mt-1">{error.find(item => item.field == "country").msg}</p> : null}
                            </div>

                            <div>
                                <label htmlFor="address" className="block mb-2 text-sm font-medium text-gray-900">Address</label>
                                <textarea value={address} onChange={(e) => setAddress(e.target.value)} name="address" id="address" rows="3" className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5" placeholder="Enter hospital full address" required=""></textarea>
                                {error.find(item => item.field == "address").msg ? <p className="text-red-500 text-sm font-semibold mt-1">{error.find(item => item.field == "address").msg}</p> : null}
                            </div>

                            <button onClick={() => signup()} type="submit" className="w-full rounded-lg bg-black px-5 py-2.5 text-center text-sm font-medium text-white hover:bg-gray-900 focus:outline-none focus:ring-4 focus:ring-gray-300">{btnState}</button>
                            
                            <p className="text-sm font-light text-gray-500">
                                Already have an account? <Link to="/login" className="font-medium text-blue-600 hover:underline">Sign in</Link>
                            </p>
                            <p className="text-sm font-light text-gray-500">
                                Are you a patient? <Link to="/signup/patient" className="font-medium text-blue-600 hover:underline">Patient Signup</Link>
                            </p>
                        </div>
        </AuthLayout>
    )
}

export default HospitalSignup

